import { useEffect, useMemo, useRef } from 'react'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { UserAvatar } from '@/components/composites/user-avatar'
import { cn } from '@/lib/utils'
import { toast } from 'sonner'
import { useGamesList, useJoin, useOpenTable, type GameInfo, type TableSummary } from './api'
import { firstName, openSeats, playerRange, type GamePerson } from './helpers'
import { useGamesDialog } from './useGamesDialog'
import { TableView } from './TableView'

function TableRow({ table, game, me, people, onOpen }: { table: TableSummary; game: GameInfo | undefined; me: string; people: Map<string, GamePerson>; onOpen: (host: string) => void }) {
  const join = useJoin(table.host)
  const seated = table.players.includes(me) && !table.gone.includes(me)
  const open = table.status === 'lobby' && openSeats(table) > 0
  const label = seated ? (table.myTurn ? 'Your move' : 'Resume') : open ? 'Join' : 'Watch'
  return (
    <li className="flex items-center gap-3 rounded-lg border border-border bg-card px-3 py-2">
      <span className="text-2xl" aria-hidden>{game?.emoji ?? '🎮'}</span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold">
          {game?.name ?? table.type} <span className="font-normal text-text-muted">· {table.host === me ? 'your table' : `${firstName(people.get(table.host))}’s table`}</span>
        </p>
        <p className="flex items-center gap-1.5 text-xs text-text-secondary">
          <span className="flex -space-x-1.5">
            {table.players.slice(0, 5).map((id) => (
              <UserAvatar key={id} person={{ id, displayName: people.get(id)?.displayName ?? 'Someone', photoUrl: people.get(id)?.photoUrl }} size="xs" className="border-background" />
            ))}
          </span>
          {table.status === 'lobby' ? `${table.players.length}/${table.max} seated · waiting to start` : table.status === 'playing' ? 'In play' : 'Finished'}
        </p>
      </div>
      <Button
        type="button"
        size="sm"
        variant={table.myTurn ? 'default' : open && !seated ? 'default' : 'outline'}
        disabled={join.isPending}
        onClick={() => {
          if (open && !seated) join.mutate(undefined, { onSuccess: () => onOpen(table.host) })
          else onOpen(table.host)
        }}
      >
        {label}
      </Button>
    </li>
  )
}

function Lobby({ me, people, onOpen }: { me: string; people: Map<string, GamePerson>; onOpen: (host: string) => void }) {
  const list = useGamesList(true)
  const openTable = useOpenTable()
  const games = useMemo(() => list.data?.games ?? [], [list.data?.games])
  const byId = useMemo(() => new Map(games.map((g) => [g.id, g])), [games])
  const tables = list.data?.tables ?? []
  const mine = tables.find((t) => t.players.includes(me) && !t.gone.includes(me) && (t.status === 'lobby' || t.status === 'playing'))
  return (
    <div className="grid gap-6">
      {mine ? (
        <div className="flex flex-wrap items-center gap-3 rounded-xl border border-brand/40 bg-brand-soft px-4 py-3">
          <span className="text-2xl" aria-hidden>{byId.get(mine.type)?.emoji}</span>
          <p className="min-w-0 flex-1 text-sm">
            You are at {mine.host === me ? 'your' : `${firstName(people.get(mine.host))}’s`} <strong>{byId.get(mine.type)?.name}</strong> table.
          </p>
          <Button type="button" size="sm" onClick={() => onOpen(mine.host)}>
            {mine.myTurn ? 'Your move' : 'Go to the table'}
          </Button>
        </div>
      ) : null}

      <section aria-label="Tables right now" className="grid gap-2">
        <h3 className="text-sm font-semibold">Tables right now</h3>
        {tables.length ? (
          <ul className="grid gap-2">
            {tables.map((t) => (
              <TableRow key={t.host} table={t} game={byId.get(t.type)} me={me} people={people} onOpen={onOpen} />
            ))}
          </ul>
        ) : (
          <p className="rounded-lg border border-dashed border-border-strong px-4 py-5 text-center text-sm text-text-muted">No tables yet. Open one below and ask someone to join.</p>
        )}
      </section>

      <section aria-label="Start a game" className="grid gap-2">
        <h3 className="text-sm font-semibold">Start a game</h3>
        <ul className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {games.map((g) => (
            <li key={g.id} className="flex flex-col gap-2 rounded-xl border border-border bg-card p-3">
              <div className="flex items-center gap-2">
                <span className="text-2xl" aria-hidden>{g.emoji}</span>
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold">{g.name}</p>
                  <p className="text-xs text-text-muted">{playerRange(g)}</p>
                </div>
              </div>
              <p className="flex-1 text-xs text-text-secondary">{g.blurb}</p>
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={openTable.isPending || Boolean(mine)}
                title={mine ? 'Leave your current table first' : undefined}
                onClick={() => openTable.mutate(g.id, { onSuccess: (r) => onOpen(r.host) })}
              >
                Open a table
              </Button>
            </li>
          ))}
        </ul>
      </section>
    </div>
  )
}

/**
 * The games room, as a dialog you can open from anywhere. Which table is showing lives in the URL, so
 * a link to a table opens it for whoever clicks it. Mounted once for the whole app, it also tells you
 * when a move is waiting for you and you are not looking at that table.
 */
export function GamesRoom({ me, people }: { me: string; people: GamePerson[] }) {
  const dialog = useGamesDialog()
  const byId = useMemo(() => new Map(people.map((p) => [p.userId, p])), [people])
  const list = useGamesList(dialog.open)
  const games = list.data?.games
  const gameName = (type: string) => games?.find((g) => g.id === type)?.name ?? 'a game'

  // A move waiting for you, when you are not already looking at that table.
  const was = useRef<Map<string, boolean> | null>(null)
  const { open: isOpen, host: openHost, openTable } = dialog
  useEffect(() => {
    const tables = list.data?.tables
    if (!tables) return
    const next = new Map(tables.map((t) => [t.host, t.myTurn]))
    const before = was.current
    was.current = next
    if (!before) return
    for (const t of tables) {
      if (!t.myTurn || before.get(t.host) === true) continue
      if (isOpen && openHost === t.host) continue
      toast(`Your move in ${gameName(t.type)}`, { id: `game-${t.host}`, description: `${t.host === me ? 'Your' : `${firstName(byId.get(t.host))}’s`} table`, action: { label: 'Play', onClick: () => openTable(t.host) } })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list.data, isOpen, openHost])

  return (
    <Dialog open={dialog.open} onOpenChange={(o) => !o && dialog.close()}>
      <DialogContent className={cn('max-h-[90vh] w-[min(96vw,64rem)] max-w-none grid-cols-[minmax(0,1fr)] overflow-y-auto sm:max-w-none')}>
        <DialogTitle className="flex items-center gap-2 text-lg">
          <span aria-hidden>🎮</span> Games room
        </DialogTitle>
        <DialogDescription className="sr-only">Open a table, join one, or watch a game with your colleagues.</DialogDescription>
        {dialog.host ? <TableView key={dialog.host} host={dialog.host} me={me} people={byId} onBack={dialog.openLobby} /> : <Lobby me={me} people={byId} onOpen={dialog.openTable} />}
      </DialogContent>
    </Dialog>
  )
}
